/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx, cssMap as unboundedCssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import {
	CommentCountElement,
	ModifiedOnElement,
	OwnedByElement,
	OwnedByGroupElement,
	ReactCountElement,
	SmartLinkSize,
	SmartLinkTheme,
	SnippetBlock,
	UnresolvedAction,
} from '@atlaskit/smart-card';
import { CardSSR as SmartCardSSR } from '@atlaskit/smart-card/ssr';
import { token } from '@atlaskit/tokens';
import {
	CardBadge,
	CardContainer,
	CornerStyle,
	useTrackedSmartCardState,
} from '@atlassian/company-hub-common';
import {
	HUB_CORNER_RADIUS_CSS_VAR,
	resolveCornerRadiusOverride,
} from '@atlassian/company-hub-common/hub-corner-radius';
import { getImageTypeToShow, ImageRenderer } from '@atlassian/company-hub-common/media-components';
import { useIsHubCornerRadiusEnabled } from '@atlassian/company-hub-common/use-is-hub-corner-radius-enabled';

import { useResolveImage } from '../../../../../../controllers/use-resolve-image';
import { useSmartCardElementsRenderState } from '../../../../../../controllers/use-smart-card-elements-render-state';
import { LinkIcon } from '../../../link-icon';
import { CardDescription, CardTitle, FontVariant } from '../../../text-elements';
import type { CommonCardProps } from '../../../types';
import { messages } from './messages';

const unboundedStyles = unboundedCssMap({
	avatar: {
		maxHeight: '24px', // prevent avatar dimension from fluctuating and affecting vertical space distribution
		marginTop: '-5px',

		// Avatar is left-indented because of left padding of ul , which is introduced by Prosemirror.
		// It was fixed for other type of smart card in packages/editor/editor-common/src/styles/shared/smart-card.ts
		// However, the FlexibleCard remains unfixed because it doesn't have a specific class name.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"span[data-smart-element='OwnedByGroup'] > ul": {
			padding: 0,
			margin: 0,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'& > li': {
				margin: 0,
			},
		},
	},
	snippetOverride: {
		color: 'inherit',

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-smart-block]': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& *': {
				color: 'inherit',
				font: token('font.body.large'),
			},
		},
	},
	smartCardWrapper: {
		height: '100%',
		// TODO: remove this once we have a proper solution for the smart card height
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'[data-smart-link-container]': {
			height: '100%',
		},
	},
});

const styles = cssMap({
	content: {
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',

		// container padding
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	imageCorner: {
		borderRadius: token('radius.large'),
	},
	imageWithPadding: {
		overflow: 'hidden',
		height: '180px',
	},
	imageNoPadding: {
		overflow: 'hidden',
		height: '200px',

		// cancel out the container padding, top, right, left
		marginTop: token('space.negative.200'),
		marginRight: token('space.negative.200'),
		marginLeft: token('space.negative.200'),

		// leave extra space between image and body
		paddingBottom: token('space.050'),
	},
	headerWithImagePadding: {
		paddingTop: token('space.100'),
		paddingLeft: token('space.100'),
	},
	headerWithImage: {
		// floats on top of the image
		display: 'flex',
		position: 'absolute',

		// compensate for the container padding, top, right
		top: token('space.200'),
		left: token('space.200'),
	},
	unresolvedAction: {
		width: '100%',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		font: token('font.body.large'),
		color: token('color.text.subtle'),
	},
	static: {
		position: 'static',
	},
	relative: {
		position: 'relative',
	},
});

const MIN_TITLE_LINES = 3;
const MAX_TITLE_LINES = 5;
const MIN_DESCRIPTION_LINES = 3;
const MAX_DESCRIPTION_LINES = 6;

const getTitleDescriptionMaxLine = ({ hasImage }: { hasImage: boolean }) => {
	let titleMaxLine = MIN_TITLE_LINES;
	let descriptionMaxLine = MIN_DESCRIPTION_LINES;

	if (!hasImage) {
		titleMaxLine += 2;
		descriptionMaxLine += 3;
	}

	if (titleMaxLine > MAX_TITLE_LINES) {
		titleMaxLine = MAX_TITLE_LINES;
	}

	if (descriptionMaxLine > MAX_DESCRIPTION_LINES) {
		descriptionMaxLine = MAX_DESCRIPTION_LINES;
	}

	return {
		titleMaxLine,
		descriptionMaxLine,
	};
};

export const LargeCardV2 = ({
	cardDimensions,
	cardId,
	link,
	isInViewMode,
	isManualVariant,
	extensionLocalId,
	imageSrc,
	imageAltText,
	contentId,
	title,
	description,
	label,
	isLabelHidden,
	labelColor,
	isLabelShown,
	isPaddingEnabled,
	isAvatarShown,
	isPublishDateShown,
	isSocialStatsShown,
	isIconShown,
	isImageShown,
	onCardSucceeded,
	onCardFailed,
	backgroundColor,
	textColor,
	borderColor,
	cornerStyle,
	cornerRadius,
	enableSnippetRenderer,
	showDescription,
}: CommonCardProps): JSX.Element => {
	const hasLink = !!link;
	const hideCorner = cornerStyle === CornerStyle.SQUARED;
	const { formatMessage } = useIntl();

	const isCornerRadiusEnabled = useIsHubCornerRadiusEnabled();
	const cornerRadiusOverride = isCornerRadiusEnabled
		? resolveCornerRadiusOverride(cornerRadius, cornerStyle)
		: undefined;

	const cardMinHeight = cardDimensions.minHeight;
	const cardMaxHeight = cardDimensions.maxHeight;

	const {
		setIsAvatarAvailable,
		setIsOwnerNameAvailable,
		setIsModifiedOnAvailable,
		setIsReactCountAvailable,
		setIsCommentCountAvailable,
		shouldRenderOwnerInfo,
		shouldRenderMetadata,
		shouldRenderSocials,
	} = useSmartCardElementsRenderState({
		isOwnerInfoShown: isAvatarShown && hasLink,
		isModifiedOnShown: isPublishDateShown && hasLink,
		isSocialStatsShown: isSocialStatsShown && hasLink,
	});

	const displayImageSrc = useResolveImage(link, imageSrc);
	// Author can hide images on Help Hub dynamic cards; suppress the resolved
	// image so the card reflows to its no-image layout.
	const { hasImage: resolvedHasImage } = getImageTypeToShow({ imageSrc: displayImageSrc });
	const hasImage = isImageShown !== false && resolvedHasImage;

	const {
		isResolved,
		errorCardStatus: hasError,
		trackAndCategorizeError,
		trackSuccess,
	} = useTrackedSmartCardState(cardId, link, onCardSucceeded, onCardFailed);

	const { titleMaxLine, descriptionMaxLine } = getTitleDescriptionMaxLine({ hasImage });

	const cardBodyItems = hasLink
		? [
				// Title
				title && (
					<CardTitle key="title" maxLine={titleMaxLine} fontVariant={FontVariant.HeadingMedium}>
						{title}
					</CardTitle>
				),
				// Unresolved action
				hasError && !title && !description && (
					<Box key="unresolved-action" xcss={styles.unresolvedAction}>
						<UnresolvedAction />
					</Box>
				),
				// Description
				// AI snippet
				showDescription && enableSnippetRenderer && (
					<div key="snippet" css={unboundedStyles.snippetOverride}>
						<SnippetBlock showFooter={false} maxLines={descriptionMaxLine} text={description} />
					</div>
				),
				// normal description
				showDescription && !enableSnippetRenderer && description && (
					<CardDescription
						key="description"
						maxLine={descriptionMaxLine}
						fontVariant={FontVariant.BodyLarge}
					>
						{description}
					</CardDescription>
				),
			].filter(Boolean)
		: [
				// Title or placeholder
				<CardTitle key="title" maxLine={titleMaxLine} fontVariant={FontVariant.HeadingMedium}>
					{title || formatMessage(messages.emptyStatePlaceholderTitle)}
				</CardTitle>,
				// Description
				description && (
					<CardDescription
						key="description"
						maxLine={descriptionMaxLine}
						fontVariant={FontVariant.BodyLarge}
					>
						{description}
					</CardDescription>
				),
			].filter(Boolean);

	const cardContent = (
		<Box
			testId="large-card-v2-content"
			xcss={cx(styles.content, hasLink ? styles.static : styles.relative)}
			style={{
				minHeight: cardMinHeight,
				maxHeight: cardMaxHeight,
			}}
		>
			<Stack spread="space-between" space="space.200" grow="fill">
				<Stack space="space.200">
					{hasImage && (
						<Box
							testId="large-card-v2-image"
							xcss={cx(
								isPaddingEnabled ? styles.imageWithPadding : styles.imageNoPadding,
								!isCornerRadiusEnabled && !hideCorner && isPaddingEnabled && styles.imageCorner,
							)}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
							style={
								isCornerRadiusEnabled
									? isPaddingEnabled
										? {
												// Padded image is inset from every card edge, so round all corners.
												// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
												borderRadius: `calc(var(${HUB_CORNER_RADIUS_CSS_VAR})/2)`,
											}
										: {
												// No-padding image is flush with the card's top / left / right
												// edges, but its bottom meets the card body — round only the top
												// corners so the bottom stays square (matches design).
												// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
												borderTopLeftRadius: `var(${HUB_CORNER_RADIUS_CSS_VAR})`,
												// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
												borderTopRightRadius: `var(${HUB_CORNER_RADIUS_CSS_VAR})`,
											}
									: undefined
							}
						>
							<ImageRenderer
								contentId={contentId}
								imageSrc={displayImageSrc}
								imageAlt={imageAltText}
								shouldShowExternalSiteImage={hasLink}
							/>
						</Box>
					)}

					{((isIconShown && hasLink) || (isLabelShown && !isLabelHidden)) && (
						<Inline
							space="space.100"
							alignBlock="center"
							xcss={cx(
								hasImage && styles.headerWithImage,
								hasImage && isPaddingEnabled && styles.headerWithImagePadding,
							)}
						>
							{isIconShown && hasLink && <LinkIcon link={link} />}
							{isLabelShown && !isLabelHidden && (
								<CardBadge label={label} labelColor={labelColor} />
							)}
						</Inline>
					)}

					<Stack testId="large-card-body" space="space.100">
						{cardBodyItems}
					</Stack>
				</Stack>

				{/* Bottom row */}
				{hasLink && !hasError && isResolved && (shouldRenderMetadata || shouldRenderSocials) && (
					<Inline alignBlock="center" spread="space-between">
						{/* Avatar and Publish Date */}
						{shouldRenderMetadata && (
							<Inline space="space.050" separator={<Box>•</Box>} alignBlock="center">
								{shouldRenderOwnerInfo && (
									<Inline space="space.050" alignBlock="center">
										<div css={unboundedStyles.avatar}>
											<OwnedByGroupElement onRender={setIsAvatarAvailable} />
										</div>
										<OwnedByElement color="inherit" hideFormat onRender={setIsOwnerNameAvailable} />
									</Inline>
								)}
								{!!isPublishDateShown && (
									<ModifiedOnElement
										color="inherit"
										hideDatePrefix
										onRender={setIsModifiedOnAvailable}
									/>
								)}
							</Inline>
						)}
						{shouldRenderSocials && (
							<Inline space="space.150">
								<ReactCountElement color="inherit" onRender={setIsReactCountAvailable} />
								<CommentCountElement color="inherit" onRender={setIsCommentCountAvailable} />
							</Inline>
						)}
					</Inline>
				)}
			</Stack>
		</Box>
	);

	return (
		<CardContainer
			id="large-card-v2-container"
			isInViewMode={isInViewMode}
			cardId={cardId}
			extensionLocalId={extensionLocalId}
			isEditable={isManualVariant}
			cornerStyle={cornerStyle}
			hasLink={hasLink}
			backgroundColor={backgroundColor}
			textColor={textColor}
			borderColor={borderColor}
			enableHubCornerRadius
			cornerRadiusOverride={cornerRadiusOverride}
		>
			{hasLink ? (
				<div css={unboundedStyles.smartCardWrapper}>
					<SmartCardSSR
						id={cardId}
						appearance="inline"
						frameStyle="show"
						ui={{
							theme: SmartLinkTheme.Black,
							clickableContainer: isInViewMode,
							size: SmartLinkSize.Large,
							removeBlockRestriction: true,
							hideBackground: true,
							hideElevation: true,
							hidePadding: true,
							enableSnippetRenderer,
						}}
						url={link}
						onResolve={trackSuccess}
						onError={trackAndCategorizeError}
					>
						{cardContent}
					</SmartCardSSR>
				</div>
			) : (
				cardContent
			)}
		</CardContainer>
	);
};
